/**
 * CAT Scale weigh-ticket CSVs uploaded through the "Create from Document" dialog.
 *
 * The server recognises the format and returns line items already shaped like
 * tbl_ExpenseLine (POST excel/preview — see App/Controllers/Documents/CONTEXT.md in the
 * backend repo). This module holds the rules that survive that hand-off: how a preview
 * item becomes an ordinary bill line, and which of those lines still need a human.
 *
 * A scale line is a bill line — description, account, amount, charge to — so it carries no
 * import-specific controls. The driver the server matched is simply who the line is
 * charged to; where it could not match one, the line is flagged and the user fills the
 * charge-to in themselves.
 *
 * Deliberately free of app imports — like vendor-match.js and item-match-helpers.js
 * beside it. Building Field instances would mean importing the form module, which drags
 * in the util-constants/localStorage import cycle and makes the file untestable. So this
 * returns plain rows and plain patches; the dialog does the Field plumbing.
 */

export const FORMAT_CAT_SCALE = 'CAT_SCALE';

/** Driver cells the server could not resolve. Mirrors CatScaleImportManager::resolveDriver. */
export const DRIVER_NOT_FOUND = 'NOT_FOUND';
export const DRIVER_AMBIGUOUS = 'AMBIGUOUS';

export function isCsvFile(file) {
    if (!file) {
        return false;
    }

    const type = String(file.type ?? '').toLowerCase();
    if (type === 'text/csv' || type === 'application/csv') {
        return true;
    }

    // Windows registers .csv as application/vnd.ms-excel when Excel is installed, and some
    // browsers hand over an empty type, so the extension is the reliable discriminator.
    return String(file.name ?? '').toLowerCase().endsWith('.csv');
}

/**
 * The two accounts a scale line can land in, as {value, label} ready for a select-search.
 * Both come from the company's accounting settings, which is also what the dialog sends
 * to the server as ExpensesAccountID / AdvancesAccountID.
 */
export function getPreviewAccounts(defaults) {
    return {
        expenses: {value: defaults?.ExpenseAccountID ?? null, label: defaults?.ExpenseAccount ?? ''},
        advances: {value: defaults?.AdvancesAccountID ?? null, label: defaults?.AdvancesAccount ?? ''},
    };
}

/** Label for an AccountID the server already chose, so the select renders filled in. */
export function accountLabelFor(accountID, defaults) {
    const accounts = getPreviewAccounts(defaults);

    if (accountID && accounts.advances.value && Number(accountID) === Number(accounts.advances.value)) {
        return accounts.advances.label;
    }
    if (accountID && accounts.expenses.value && Number(accountID) === Number(accounts.expenses.value)) {
        return accounts.expenses.label;
    }

    return '';
}

/**
 * Preview items -> flat rows for getExpenseItemField. The mirror of itemsFromExtraction's
 * input on the AI path; unlike that one it needs no Qty/Amount repair, because the server
 * sends one ticket per line with Qty 1 and a real Fee.
 */
export function catScaleRowsFromPreview(previewItems, defaults) {
    return (previewItems ?? []).map((it) => {
        const amount = Number(it.Amount) || 0;
        const driverError = it.Driver?.error ?? '';

        return {
            Description: it.Description ?? '',
            AccountID: it.AccountID ?? null,
            Account: accountLabelFor(it.AccountID, defaults),
            Amount: amount,
            Qty: Number(it.Qty) || 1,
            TotalAmount: Number(it.TotalAmount) || amount,

            // No ChargeTo discriminator here on purpose: getExpenseItemField derives it from
            // the FKs below (chargeToFromItem), so there is one source of truth. The server
            // sets a contact only for a resolved driver who pays their own fuel; everything
            // else stays on the company.
            ChargeToContactID: it.ChargeToContactID ?? '',
            // Paired label for fillFieldsFromData, so the Charge To select renders filled in.
            ChargeToContact: it.ChargeToContactID ? (it.Driver?.name ?? '') : '',

            // Not columns — the name off the ticket and why it could not be matched, kept so
            // the warning can say whose line needs attention.
            DriverName: it.Driver?.name ?? (driverError ? (it.Driver?.value ?? '') : ''),
            DriverWarning: driverError,
        };
    });
}

/**
 * Banner data: how many lines still need a driver, and which names to show. Reads the
 * dialog's Field maps defensively so it works on a live, partly-fixed list.
 */
export function catScaleWarningSummary(items) {
    const unresolved = (items ?? []).filter((it) => !!it?.DriverWarning?.value);

    return {
        count: unresolved.length,
        names: Array.from(new Set(unresolved.map((it) => it?.DriverName?.value).filter(Boolean))),
        hasAmbiguous: unresolved.some((it) => it.DriverWarning.value === DRIVER_AMBIGUOUS),
    };
}
